// Deep-link helpers from a run/session to its external observability surfaces:
//   - the Langfuse TRACE (`/project/<pid>/traces?peek=<traceId>`), where the
//     trace id is the deterministic `sha256(sessionId)` first-16-bytes hex (the
//     same algorithm the platform uses in `@sb-ai-platform/observability`'s
//     `deterministicTraceIdFromSession`); and
//   - the Anthropic Managed-Agents BACKEND session page
//     (`<ma-console>/<provider_session_ref>`), only for MA-backed sessions.
//
// Langfuse base + project id are per-env and baked into the bundle at build via
// `VITE_LANGFUSE_BASE_URL` / `VITE_LANGFUSE_PROJECT_ID` (set on the Console
// StaticSite in `infra/sst/lib/static-site.ts`); when unset the Langfuse link is
// simply hidden. The MA console base defaults to the public host and is
// overridable via `VITE_MA_CONSOLE_BASE_URL`.

function readEnv(key: string): string | undefined {
  // `import.meta.env` is the Vite build-time source the browser bundle reads.
  const meta = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
  let raw: unknown = meta?.[key];
  // Test fallback: vitest's `vi.stubEnv` writes `process.env`, not the per-module
  // `import.meta.env`. Read it via globalThis so the browser bundle (no `process`)
  // never throws and needs no node types.
  if (typeof raw !== 'string') {
    raw = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.[
      key
    ];
  }
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim();
  return v.length > 0 ? v : undefined;
}

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

/** Deterministic Langfuse trace id for a platform session id — `sha256(id)[:32]`. */
async function traceIdFromSession(sessionId: string): Promise<string | undefined> {
  const subtle = globalThis.crypto?.subtle;
  if (typeof sessionId !== 'string' || sessionId.length === 0 || subtle?.digest === undefined) {
    return undefined;
  }
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(sessionId));
    return [...new Uint8Array(digest)]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 32);
  } catch {
    return undefined;
  }
}

/** Both Langfuse env values present → the trace link can be rendered. */
export function langfuseConfigured(): boolean {
  return (
    readEnv('VITE_LANGFUSE_BASE_URL') !== undefined &&
    readEnv('VITE_LANGFUSE_PROJECT_ID') !== undefined
  );
}

/** Build the Langfuse trace deep-link, or `undefined` if unconfigured / no id. */
export function langfuseTraceHref(traceId: string | null | undefined): string | undefined {
  const base = readEnv('VITE_LANGFUSE_BASE_URL');
  const pid = readEnv('VITE_LANGFUSE_PROJECT_ID');
  if (
    base === undefined ||
    pid === undefined ||
    traceId === undefined ||
    traceId === null ||
    traceId.length === 0
  ) {
    return undefined;
  }
  return `${stripTrailingSlash(base)}/project/${encodeURIComponent(pid)}/traces?peek=${encodeURIComponent(traceId)}`;
}

/** Resolve a session's deterministic trace id, then open its Langfuse trace. */
export async function openLangfuseTrace(sessionId: string): Promise<void> {
  if (!langfuseConfigured()) return;
  const href = langfuseTraceHref(await traceIdFromSession(sessionId));
  if (href !== undefined) window.open(href, '_blank', 'noopener,noreferrer');
}

const MA_CONSOLE_DEFAULT = 'https://platform.claude.com/workspaces/default/sessions';

/**
 * MA backend session-page URL for a `provider_session_ref`, or `undefined` for a
 * non-MA session. The MA provider ref is a `sesn_…` id; that shape gates the link
 * so other providers' refs never produce a (wrong) platform.claude.com URL.
 */
export function maSessionUrl(providerSessionRef: string | null | undefined): string | undefined {
  if (typeof providerSessionRef !== 'string' || !providerSessionRef.startsWith('sesn_')) {
    return undefined;
  }
  const base = stripTrailingSlash(readEnv('VITE_MA_CONSOLE_BASE_URL') ?? MA_CONSOLE_DEFAULT);
  return `${base}/${encodeURIComponent(providerSessionRef)}`;
}
